import React, { useCallback, useMemo } from 'react';
import { classNames, shortNumber } from '@fullstory/util/fmt';
import { getPercentString } from '../../../util/fmt';
import css from '../index.module.scss';
import {
  Action,
  ActionMenu,
  Box,
  Divider,
  Flex,
  InlineLink,
  Text,
  Tooltip,
} from '@fullstory/components';
import {
  BOX_WIDTH,
  BOX_TOP_BAR_HEIGHT,
  TOTAL_BOX_HEIGHT,
  hideAction,
  groupAction,
  groupPageAction,
  groupElementAction,
} from '../const';
import {
  ActionCallbacks,
  GraphNode,
  JourneyMapGraphicProps,
  NodeType,
  NodeTypeToString,
  RevolveLabel,
} from '../types';
import { EventIdentValues } from '@fullstory/proto/fs/api/segment';
import { int64 } from '@fullstory/proto/google/protobuf';
import { EntryBar } from './EntryBar';
import { ExitBar } from './ExitBar';
import { Label, SubLabel } from './Label';
import { formatNumber, OTHER_LABEL, PageNotDefinedInfo } from '@fullstory/library-ui';
import { DefinedEventField } from '@fullstory/client-api';
import { FormattedMessage, useIntl } from 'react-intl';
import { getVarSublabel } from '../util';
import { uniqBy } from 'lodash';
import { getLabelForIdent, getTypeForIdent, getSublabelForIdent } from '../../../util/ident';

type NodeExtensibleJourneyMapGraphicProps =
  | 'customVars'
  | 'pivotDep'
  | 'deleteFilter'
  | 'filters'
  | 'getGroupByPropsDisabled'
  | 'getNamedElement'
  | 'getNamedElementTooltip'
  | 'getDefinedEvent'
  | 'getDefinedEventTooltip'
  | 'getApiEventTooltip'
  | 'getPageDefTooltip'
  | 'getPageName'
  | 'pageVars'
  | 'setFilters'
  | 'viewMetric'
  | 'elemVars';

interface JourneyMapNodeProps
  extends Pick<JourneyMapGraphicProps, NodeExtensibleJourneyMapGraphicProps> {
  canHideEvent: boolean;
  /**
   * entryDropin and exitDropoff represent how many didn't make it to the next interaction,
   * as a ratio to the count on the current node - (node.count - node.nextCount) * 100 / node.count.
   * These will also be passed in to by the graph.
   */
  entryDropin?: number;
  /**
   * Outgoing bar are heights of the black bars for the outgoing counts of the node.
   * The graph will pass this in by calculating the total heights of all the relevant links.
   */
  exitOffset: number;
  exitDropoff?: number;
  actionCallbacks?: ActionCallbacks;
  identity?: EventIdentValues;
  isNodeInPath?: boolean;
  /** The depth of the deepest node of the graph this node belongs to */
  maxDepth: number;
  node: GraphNode;
  numerator: number;
  denominator: number;
  isFirstStep: boolean;
  selected?: boolean;
  showOtherEventsForDepth?: (depth: number) => void;
}

const ICON_SIZE = 16;

export const Node: React.FC<JourneyMapNodeProps> = ({
  canHideEvent,
  customVars,
  pivotDep,
  deleteFilter,
  entryDropin,
  exitDropoff,
  exitOffset,
  filters,
  getGroupByPropsDisabled,
  getNamedElement,
  getNamedElementTooltip,
  getDefinedEvent,
  getDefinedEventTooltip,
  getApiEventTooltip,
  getPageDefTooltip,
  getPageName,
  actionCallbacks,
  identity,
  isNodeInPath = true,
  maxDepth,
  node,
  pageVars,
  numerator,
  denominator,
  isFirstStep,
  selected = false,
  setFilters,
  viewMetric,
  showOtherEventsForDepth,
  elemVars,
}) => {
  const intl = useIntl();
  const subLabels: [string, string][] = [];
  const pageDefId = int64.toNumber(identity?.values[0]?.longValue);
  const stringId = identity?.values[0]?.stringValue;

  const label = identity
    ? getLabelForIdent(identity, getPageName, getNamedElement, getDefinedEvent)
    : '';
  const type = identity ? getTypeForIdent(identity) : NodeType.Other;

  if (identity) {
    // Push event prop value sublabel if it's a custom or pageview pivot (correct type and can't hide) and a pivotDep exists
    if (
      (identity.type === 'custom' ||
        identity.type === 'pageview' ||
        identity.type === 'click' ||
        identity.type === 'seen') &&
      pivotDep &&
      !canHideEvent
    ) {
      const sublabel = getVarSublabel(pivotDep);
      if (sublabel) {
        subLabels.push(sublabel);
      }
    }
    // And then push any sublabel for group by vars
    const sublabel = getSublabelForIdent(identity, customVars, pageVars, elemVars);
    if (sublabel) {
      subLabels.push(sublabel);
    }
  }

  // Remove duplicate sublabels in the case that a custom event is grouped by the same prop as the pivot dep
  const uniqueSublabels = uniqBy(subLabels, label => label.toString());

  const currentFilterDepth = filters
    ? Math.max(...Object.keys(filters).map(f => parseInt(f, 10)))
    : 0;

  // If we have a filter set for anything past this point, then entry and exit do not apply.
  // Likewise, for a filter set at this point that isn't an entry or exit.
  const isEntryOrExitDimAtPosition = useCallback(
    (currentDepth: number, position: number, entryOrExit: RevolveLabel): boolean =>
      currentDepth > position ||
      (!!filters?.[position]?.hash && filters[position]?.hash !== entryOrExit),
    [filters],
  );

  // Sets or removes the filter for the entry or exit sessions.
  // If setting the filter, also set the filter for the node behind this entry or exit.
  const entryExitAction = useCallback(
    (e: React.MouseEvent, position: number, entryOrExit: RevolveLabel) => {
      e.stopPropagation();
      if (filters?.[position]?.hash === entryOrExit) {
        deleteFilter?.(position);
      } else {
        // enter/exit before this interaction
        setFilters?.({
          [position]: { values: null, hash: entryOrExit },
          [position - 1]: { values: identity ?? null, hash: node.identHash },
        });
      }
    },
    [deleteFilter, filters, identity, node.identHash, setFilters],
  );

  const actionItems: ReadonlyArray<Readonly<Action>> = useMemo(() => {
    const actions = [];
    if (canHideEvent && type !== NodeType.Other) {
      actions.push(hideAction);
    }
    switch (type) {
      case NodeType.ApiEvent: {
        const eventName = stringId;
        if (eventName) {
          actions.push({
            ...groupAction,
            disabled: getGroupByPropsDisabled.event(eventName),
          });
        }
        break;
      }
      case NodeType.Page: {
        if (pageDefId !== 0) {
          actions.push({
            ...groupPageAction,
            disabled: getGroupByPropsDisabled.page(pageDefId),
          });
        }
        break;
      }
      case NodeType.ClickNamed:
      case NodeType.SeenNamed: {
        const namedElementId = stringId;
        if (namedElementId) {
          actions.push({
            ...groupElementAction,
            disabled: getGroupByPropsDisabled.element(namedElementId),
          });
        }
      }
    }
    return actions;
  }, [canHideEvent, type, pageDefId, getGroupByPropsDisabled, stringId]);

  const onSelectAction = useCallback(
    (id: string) => {
      switch (id) {
        case 'hide':
          identity && actionCallbacks?.handleHiddenEvents?.(true, label, identity, type);
          break;
        case 'group': {
          const eventName = stringId;
          eventName && actionCallbacks?.handleGroupEvent?.(eventName);
          break;
        }
        case 'groupPage':
          actionCallbacks?.handleGroupPage?.(pageDefId);
          break;
        case 'groupElement': {
          const namedElementId = stringId;
          namedElementId && actionCallbacks?.handleGroupElement?.(namedElementId);
          break;
        }
      }
    },
    [identity, stringId, label, type, pageDefId, actionCallbacks],
  );

  return (
    <>
      {!!entryDropin && entryDropin > 0 && (
        <EntryBar
          height={entryDropin}
          onClick={entryExitAction}
          node={node}
          dimmed={isEntryOrExitDimAtPosition(
            currentFilterDepth,
            maxDepth - node.depth + 1,
            RevolveLabel.ENTRY,
          )}
          maxDepth={maxDepth}
        />
      )}

      <rect
        x={0}
        y={0}
        width={BOX_WIDTH}
        height={TOTAL_BOX_HEIGHT}
        style={{ filter: 'url(#journeyMapNodeFilter)' }}
        className={classNames(css.node, { [css.selected]: selected })}
        rx="4"
      />
      <path
        d={`M0,${selected ? 1 : 0}
          h${BOX_WIDTH - (selected ? 5 : 4)}
          q4,0 4,4
          v${BOX_TOP_BAR_HEIGHT - 4}
          h-${BOX_WIDTH - (selected ? 2 : 0)}
          v-${BOX_TOP_BAR_HEIGHT - 4}
          q0,-4 4,-4
          z
        `}
        className={classNames({
          [css.page]: type === NodeType.Page,
          [css.custom]: type === NodeType.ApiEvent || type === NodeType.DefinedEvent,
          [css.named]: type === NodeType.SeenNamed || type === NodeType.ClickNamed,
          [css.other]: type === NodeType.Other,
        })}
      />
      <foreignObject
        width={BOX_WIDTH}
        height={TOTAL_BOX_HEIGHT}
        className={classNames(css.active, { [css.click]: !!type })}
        data-testid="journey-map-node-container"
      >
        <Box
          padding={[8, 4, 0, 12]}
          style={{ whiteSpace: 'nowrap' }}
          testId={pageDefId === 0 ? 'not-defined-node' : undefined}
        >
          <Flex justifyContent="space-between" alignItems="flex-start">
            <Box paddingTop={8} alignSelf="flex-end">
              <Text appearance="bodyXSmall" color="secondary">
                {NodeTypeToString[type]}
              </Text>
            </Box>
            <Box paddingTop={4} onClick={e => e.stopPropagation()}>
              <Flex>
                {pageDefId === 0 && type === NodeType.Page && (
                  <PageNotDefinedInfo size={ICON_SIZE} viewMetric={viewMetric} />
                )}
                {!!pageDefId && getPageDefTooltip(pageDefId)}
                {(identity?.type === 'click' || identity?.type === 'seen') &&
                  getNamedElementTooltip(identity.values[0]?.stringValue || '')}
                {identity?.type === DefinedEventField &&
                  getDefinedEventTooltip(identity.values[0]?.stringValue || '')}
                {identity?.type === 'custom' &&
                  getApiEventTooltip(identity.values[0]?.stringValue || '')}
                {actionItems.length > 0 && actionCallbacks && (
                  <ActionMenu
                    actions={actionItems}
                    onSelect={onSelectAction}
                    iconColor="secondary"
                  />
                )}
              </Flex>
            </Box>
          </Flex>
          <Box paddingRight={8}>
            <Box height={48} display="flex" flexDirection="column" gap={4}>
              {type === NodeType.Other ? (
                <Text appearance="bodyMediumBold">
                  {node.otherCount && showOtherEventsForDepth && currentFilterDepth < 0 ? (
                    <InlineLink onClick={() => showOtherEventsForDepth(node.depth)}>
                      {intl.formatMessage(
                        { defaultMessage: '{numOther, number} more events', id: 'KS4+lU' },
                        { numOther: node.otherCount },
                      )}
                    </InlineLink>
                  ) : (
                    OTHER_LABEL
                  )}
                </Text>
              ) : (
                <Label label={label} noWrap={!!uniqueSublabels.length} />
              )}
              {uniqueSublabels.map(label => (
                <SubLabel key={label.toString()} parts={label} />
              ))}
            </Box>
            {isNodeInPath ? (
              <Box testId="node-data" className="fs-mask" display="flex" gap={4}>
                <Text appearance="bodySmall" color="emphasized" tag="div">
                  <Tooltip
                    placement="bottom-start"
                    className={classNames(css.nodeTooltip, 'fs-mask')}
                    offset={[-8, 0]}
                    tooltipContent={
                      <Box width={248} display="flex" flexDirection="column" gap={4}>
                        <Text appearance="bodySmall" color="default-inverse">
                          <FormattedMessage
                            defaultMessage="{numerator} · Current step"
                            id="HHjHky"
                            values={{
                              numerator: formatNumber(numerator, undefined),
                            }}
                          />
                        </Text>
                        <Divider />
                        <Text appearance="bodySmall" color="default-inverse">
                          <FormattedMessage
                            defaultMessage="{denominator} · {isFirstStep, select, true {Step total} other {Previous step}}"
                            id="Xn9DkN"
                            values={{
                              denominator: formatNumber(denominator, undefined),
                              isFirstStep,
                            }}
                          />
                        </Text>
                      </Box>
                    }
                  >
                    {getPercentString(numerator, denominator)}
                  </Tooltip>
                </Text>
                <Text appearance="bodySmall" color="default" tag="div">
                  <FormattedMessage
                    defaultMessage="·"
                    id="G7uas+"
                    description="Used as a separator e.g 10% · 100 views"
                  />
                </Text>
                <Text appearance="bodySmall" color="default" tag="div">
                  <FormattedMessage
                    defaultMessage="{nodeCount}"
                    id="RAQXgO"
                    values={{
                      nodeCount: shortNumber(node.count),
                    }}
                  />
                </Text>
              </Box>
            ) : null}
          </Box>
        </Box>
      </foreignObject>
      {!!exitDropoff && exitDropoff > 0 && (
        <ExitBar
          height={exitDropoff}
          offset={exitOffset}
          onClick={entryExitAction}
          node={node}
          dimmed={isEntryOrExitDimAtPosition(currentFilterDepth, node.depth + 1, RevolveLabel.EXIT)}
        />
      )}
    </>
  );
};
